// UTM tracking utilities — capture, persist (sessionStorage), and apply to URLs/forms.

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export type UTMKey = (typeof UTM_KEYS)[number];
export type UTMParams = Partial<Record<UTMKey, string>>;

const STORAGE_KEY = "utm_params";

function isBrowser() {
  return typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";
}

/** Read UTM params from the current URL (or a provided search string). */
export function readUTMFromURL(search?: string): UTMParams {
  if (!isBrowser() && !search) return {};
  const qs = search ?? window.location.search;
  const params = new URLSearchParams(qs);
  const out: UTMParams = {};
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v) out[k] = v;
  }
  return out;
}

/** Get UTM params currently saved in sessionStorage. */
export function getUTMParams(): UTMParams {
  if (!isBrowser()) return {};
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed as UTMParams;
  } catch {
    /* ignore */
  }
  return {};
}

/** Save UTM params to sessionStorage (overwrites). */
export function saveUTMParams(params: UTMParams): void {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
  } catch {
    /* ignore */
  }
}

/**
 * Capture UTM params from the current URL.
 * - If URL has UTMs → overwrite saved ones.
 * - If URL has none but session has some → keep saved (no-op).
 */
export function captureUTMParams(): UTMParams {
  if (!isBrowser()) return {};
  const fromURL = readUTMFromURL();
  if (Object.keys(fromURL).length > 0) {
    saveUTMParams(fromURL);
    return fromURL;
  }
  return getUTMParams();
}

/**
 * Merge saved UTM params into a URL (absolute or relative).
 * Existing params on the URL win — no duplicates. Returns url unchanged when no UTMs saved.
 */
export function appendUTMToURL(url: string, extra?: UTMParams): string {
  if (!url) return url;
  const utms = { ...getUTMParams(), ...(extra ?? {}) };
  const keys = Object.keys(utms).filter((k) => utms[k as UTMKey]);
  if (keys.length === 0) return url;

  // Skip non-http/mailto/tel schemes handled naturally — allow http(s), relative, and
  // links like WhatsApp wa.me (https). Skip mailto:, tel:, javascript:, hash-only.
  const lower = url.toLowerCase().trim();
  if (
    lower.startsWith("mailto:") ||
    lower.startsWith("tel:") ||
    lower.startsWith("javascript:") ||
    lower.startsWith("#")
  ) {
    return url;
  }

  const isAbsolute = /^https?:\/\//i.test(url);
  const base = isBrowser() ? window.location.origin : "http://localhost";
  let u: URL;
  try {
    u = isAbsolute ? new URL(url) : new URL(url, base);
  } catch {
    return url;
  }

  for (const k of keys) {
    if (!u.searchParams.has(k)) {
      u.searchParams.set(k, utms[k as UTMKey] as string);
    }
  }

  if (isAbsolute) return u.toString();
  // Preserve relative form
  return `${u.pathname}${u.search}${u.hash}`;
}

/** Returns UTM params as an object with only the defined keys — useful for form payloads. */
export function utmToFormFields(): UTMParams {
  const utms = getUTMParams();
  const out: UTMParams = {};
  for (const k of UTM_KEYS) {
    if (utms[k]) out[k] = utms[k];
  }
  return out;
}

/** Fixed outbound UTMs applied to any link pointing to logcomex.ai (main site). */
export const OUTBOUND_LOGCOMEX_UTMS: UTMParams = {
  utm_source: "blog",
  utm_medium: "referral",
  utm_campaign: "blog-logcomex-ai",
};

/** True when the href resolves to a logcomex.ai host (any subdomain except blog.*). */
export function isOutboundToLogcomex(href: string): boolean {
  if (!href) return false;
  const lower = href.toLowerCase().trim();
  if (!/^https?:\/\//i.test(lower)) return false;
  try {
    const u = new URL(href);
    const host = u.hostname.toLowerCase();
    if (host === "blog.logcomex.ai") return false; // same site
    return host === "logcomex.ai" || host.endsWith(".logcomex.ai");
  } catch {
    return false;
  }
}

/**
 * Merge given UTMs into a URL WITHOUT overwriting any query param already present.
 * Returns url unchanged when there is nothing to add.
 */
export function mergeParamsIntoURL(url: string, params: UTMParams): string {
  if (!url) return url;
  const keys = Object.keys(params).filter((k) => params[k as UTMKey]);
  if (keys.length === 0) return url;
  const isAbsolute = /^https?:\/\//i.test(url);
  const base = isBrowser() ? window.location.origin : "http://localhost";
  let u: URL;
  try {
    u = isAbsolute ? new URL(url) : new URL(url, base);
  } catch {
    return url;
  }
  for (const k of keys) {
    if (!u.searchParams.has(k)) {
      u.searchParams.set(k, params[k as UTMKey] as string);
    }
  }
  return isAbsolute ? u.toString() : `${u.pathname}${u.search}${u.hash}`;
}

